// ═══════════════════════════════════════════════════════════════
// PricingPage — Page abonnement / tarifs (chantier #5)
// 3 paliers + palier courant + quotas consommés + upgrade/portail.
// S'appuie sur GET /api/quota/me et POST /api/billing/{checkout,portal}.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { Check, Minus, Sparkles, Loader2, Settings2 } from 'lucide-react';
import { getQuota } from '../../services/quotaApi';
import { startCheckout, openPortal } from '../../services/billingApi';
import { toast } from '../ui/Toast';

const RANK = { freemium: 0, entree: 1, partenaire: 2 };

const TIERS = [
    {
        key: 'freemium', name: 'Découverte', price: '0 €', period: '',
        tagline: 'Pour explorer ITRIIA',
        perks: [
            { ok: true, label: '1 projet' },
            { ok: true, label: '~30 messages / mois' },
            { ok: true, label: 'Chat & mémoire' },
            { ok: false, label: 'Studio, Coffre-fort, Connecteurs' },
        ],
    },
    {
        key: 'entree', name: 'Entrée', price: '14,90 €', period: '/ mois',
        tagline: 'Usage personnel régulier',
        perks: [
            { ok: true, label: 'Messages illimités' },
            { ok: true, label: 'Enveloppe LLM incluse' },
            { ok: true, label: 'Chat & mémoire complets' },
            { ok: false, label: 'Features premium (Studio…)' },
        ],
    },
    {
        key: 'partenaire', name: 'Partenaire', price: '69 €', period: '/ mois',
        tagline: 'Pour créer et publier', highlight: true,
        perks: [
            { ok: true, label: 'Tout l\'Entrée, sans limite' },
            { ok: true, label: 'ITRIIA Studio (images + voix)' },
            { ok: true, label: 'Coffre-fort chiffré' },
            { ok: true, label: 'Connecteurs & agents' },
            { ok: true, label: 'BYOK — ta clé, ta conso' },
        ],
    },
];

function QuotaBar({ label, current, max, unlimited }) {
    const pct = unlimited || !max ? 0 : Math.min(100, Math.round((current / max) * 100));
    return (
        <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 6 }}>
                <span>{label}</span>
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>
                    {unlimited ? 'Illimité' : `${current} / ${max}`}
                </span>
            </div>
            <div style={{ height: 6, borderRadius: 4, background: 'var(--input-border)', overflow: 'hidden' }}>
                <div style={{
                    width: unlimited ? '100%' : `${pct}%`, height: '100%',
                    background: unlimited ? 'linear-gradient(90deg,var(--accent),#3b82f6)' : (pct >= 100 ? '#dc2626' : 'var(--accent)'),
                    transition: 'width .4s',
                }} />
            </div>
        </div>
    );
}

export default function PricingPage({ quota: quotaProp, onRefresh }) {
    const [quota, setQuota] = useState(quotaProp || null);
    const [loading, setLoading] = useState(!quotaProp);
    const [busy, setBusy] = useState(null); // tier en cours de checkout

    const load = useCallback(async () => {
        try { setQuota(await getQuota()); } catch (e) { /* silencieux */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { if (!quotaProp) load(); }, [quotaProp, load]);
    useEffect(() => { if (quotaProp) setQuota(quotaProp); }, [quotaProp]);

    const currentTier = quota?.subscription?.tier || 'freemium';
    const hasActiveSub = !!quota?.subscription?.active;

    const handleUpgrade = async (tier) => {
        setBusy(tier);
        try { await startCheckout(tier); }
        catch (e) { toast.error(e.message || 'Impossible de démarrer le paiement.'); setBusy(null); }
    };
    const handlePortal = async () => {
        setBusy('portal');
        try { await openPortal(); }
        catch (e) { toast.error(e.message || 'Impossible d\'ouvrir le portail.'); setBusy(null); }
    };

    if (loading) {
        return (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
                <Loader2 size={22} className="spin" style={{ marginRight: 10 }} /> Chargement de ton abonnement…
            </div>
        );
    }

    return (
        <div style={{ height: '100%', overflow: 'auto', padding: '36px 28px 60px' }}>
            <div style={{ maxWidth: 1040, margin: '0 auto' }}>

                {/* En-tête */}
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <h1 style={{ margin: '0 0 8px', fontSize: 30, fontWeight: 700, color: 'var(--text)' }}>
                        Choisis ton offre
                    </h1>
                    <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 15 }}>
                        Fais grandir ton espace ITRIIA au rythme de tes besoins.
                    </p>
                </div>

                {/* Statut courant + quotas */}
                {quota && (
                    <div style={{
                        background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 14,
                        padding: '18px 22px', marginBottom: 30, display: 'flex', flexWrap: 'wrap',
                        gap: 22, alignItems: 'center',
                    }}>
                        <div style={{ minWidth: 150 }}>
                            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 4 }}>Offre actuelle</div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', textTransform: 'capitalize' }}>
                                {currentTier === 'freemium' ? 'Découverte' : currentTier}
                                {hasActiveSub && <span style={{ marginLeft: 8, fontSize: 11, color: '#16a34a' }}>● actif</span>}
                            </div>
                        </div>
                        <QuotaBar label="Messages / mois" current={quota.messages.current} max={quota.messages.max} unlimited={quota.messages.unlimited} />
                        <QuotaBar label="Projets" current={quota.projects.current} max={quota.projects.max} unlimited={quota.projects.unlimited} />
                        {hasActiveSub && (
                            <button onClick={handlePortal} disabled={busy === 'portal'} style={{
                                display: 'flex', alignItems: 'center', gap: 7, background: 'transparent',
                                color: 'var(--text)', border: '1px solid var(--card-border)', borderRadius: 9,
                                padding: '9px 15px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                            }}>
                                <Settings2 size={15} /> Gérer mon abonnement
                            </button>
                        )}
                    </div>
                )}

                {/* Cartes paliers */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
                    {TIERS.map((t) => {
                        const isCurrent = t.key === currentTier;
                        const isUpgrade = RANK[t.key] > RANK[currentTier];
                        return (
                            <div key={t.key} style={{
                                position: 'relative',
                                background: 'var(--card-bg)',
                                border: t.highlight ? '1.5px solid var(--accent)' : '1px solid var(--card-border)',
                                borderRadius: 16, padding: '26px 24px',
                                boxShadow: t.highlight ? 'var(--accent-glow)' : 'var(--shadow)',
                                display: 'flex', flexDirection: 'column',
                            }}>
                                {t.highlight && (
                                    <div style={{
                                        position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)',
                                        background: 'linear-gradient(135deg, var(--accent), #3b82f6)', color: '#fff',
                                        fontSize: 11, fontWeight: 700, letterSpacing: '.04em', padding: '4px 12px',
                                        borderRadius: 20, display: 'flex', alignItems: 'center', gap: 5,
                                    }}>
                                        <Sparkles size={12} /> POPULAIRE
                                    </div>
                                )}

                                <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>{t.name}</div>
                                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14, minHeight: 18 }}>{t.tagline}</div>
                                <div style={{ marginBottom: 18 }}>
                                    <span style={{ fontSize: 30, fontWeight: 800, color: 'var(--text)' }}>{t.price}</span>
                                    <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}> {t.period}</span>
                                </div>

                                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 22px', flex: 1 }}>
                                    {t.perks.map((p, i) => (
                                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 11, fontSize: 13.5, color: p.ok ? 'var(--text)' : 'var(--text-muted)' }}>
                                            {p.ok
                                                ? <Check size={16} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} />
                                                : <Minus size={16} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 1 }} />}
                                            {p.label}
                                        </li>
                                    ))}
                                </ul>

                                {/* CTA */}
                                {isCurrent ? (
                                    <div style={{
                                        textAlign: 'center', padding: '12px', borderRadius: 10, fontSize: 14, fontWeight: 600,
                                        background: 'var(--accent-light)', color: 'var(--accent)',
                                    }}>
                                        Offre actuelle
                                    </div>
                                ) : isUpgrade && t.key !== 'freemium' ? (
                                    <button onClick={() => handleUpgrade(t.key)} disabled={busy === t.key} style={{
                                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                        background: t.highlight ? 'linear-gradient(135deg, var(--accent), #3b82f6)' : 'transparent',
                                        color: t.highlight ? '#fff' : 'var(--accent)',
                                        border: t.highlight ? 'none' : '1.5px solid var(--accent)',
                                        borderRadius: 11, padding: '13px', fontSize: 14.5, fontWeight: 600,
                                        cursor: busy === t.key ? 'wait' : 'pointer',
                                        boxShadow: t.highlight ? 'var(--accent-glow)' : 'none',
                                    }}>
                                        {busy === t.key ? <Loader2 size={16} className="spin" /> : <>Choisir {t.name}</>}
                                    </button>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '12px', borderRadius: 10, fontSize: 13.5, color: 'var(--text-muted)' }}>
                                        {t.key === 'freemium' ? 'Offre de base' : 'Inclus'}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <p style={{ textAlign: 'center', marginTop: 26, fontSize: 12.5, color: 'var(--text-muted)' }}>
                    Paiement sécurisé par Stripe. Résiliable à tout moment depuis « Gérer mon abonnement ».
                </p>
            </div>
        </div>
    );
}
